import React, { useState, useEffect } from 'react';
import {
  client,
  createCart,
  fetchCart,
  addLineItems,
  removeLineItems,
  updateLineItems,
} from '~/services/checkout';
import Context from '~/context/StoreContext';

const CheckoutProvider = ({ children }) => {
  // Initial state
  const initialStoreState = {
    client,
    isLoading: false,
    lastAdd: null,
    checkout: {
      lineItems: [],
    },
    products: [],
    shop: {},
  };

  // State
  const [store, updateStore] = useState(initialStoreState);

  useEffect(() => {
    // Initialize Cart
    const initializeCart = async () => {
      // Check for an existing cart.
      const isBrowser = typeof window !== 'undefined';

      // Existing cart
      const existingCartID = isBrowser
        ? localStorage.getItem('shopify_cart_id')
        : null;

      // Cart
      const setCartInState = (cart) => {
        if (isBrowser) {
          localStorage.setItem('shopify_cart_id', cart.id);
        }

        updateStore((prevState) => ({ ...prevState, checkout: cart }));
      };

      if (existingCartID) {
        try {
          const cart = await fetchCart(existingCartID);
          setCartInState(cart);
          return;
        } catch (e) {
          localStorage.setItem('shopify_cart_id', null);
        }
      }

      const newCart = await createCart();
      setCartInState(newCart);
    };

    initializeCart();
  }, []);

  return (
    <Context.Provider
      value={{
        store,
        addVariantToCart: async (variantId, quantity) => {
          if (variantId === '' || !quantity) {
            console.error('Both a size and quantity are required.');
            return;
          }

          const lineItemsToUpdate = [
            { variantId, quantity: parseInt(quantity, 10) },
          ];

          updateStore((prevState) => ({ ...prevState, isLoading: true }));

          const checkout = await addLineItems(
            store.checkout.id,
            lineItemsToUpdate
          );

          updateStore((prevState) => ({
            ...prevState,
            checkout,
            isLoading: false,
            lastAdd: Date.now(),
          }));
        },
        removeLineItem: async (lineItemId) => {
          updateStore((prevState) => ({ ...prevState, isLoading: true }));

          const checkout = await removeLineItems(store.checkout.id, [
            lineItemId,
          ]);

          updateStore((prevState) => ({
            ...prevState,
            checkout,
            isLoading: false,
          }));
        },
        updateLineItem: async (lineItemId, quantity) => {
          const lineItemsToUpdate = [
            { id: lineItemId, quantity: parseInt(quantity, 10) },
          ];

          updateStore((prevState) => ({ ...prevState, isLoading: true }));

          const checkout = await updateLineItems(
            store.checkout.id,
            lineItemsToUpdate
          );

          updateStore((prevState) => ({
            ...prevState,
            checkout,
            isLoading: false,
          }));
        },
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default CheckoutProvider;
